import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase, api } from "./client";

const Ctx = createContext(null);
export const useStaff = () => useContext(Ctx);

export const StaffAuthProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, staff: null, error: "" });

  const verify = useCallback(async (session) => {
    if (!session) {
      setState((s) => ({ ...s, loading: false, staff: null }));
      return;
    }
    try {
      const staff = await api("/staff/session", { method: "POST" });
      setState({ loading: false, staff, error: "" });
    } catch (e) {
      await supabase.auth.signOut();
      setState({ loading: false, staff: null, error: e.status === 403 ? e.message : "Unable to verify staff session." });
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => verify(data.session));
    const { data } = supabase.auth.onAuthStateChange((evt, session) => {
      if (evt === "SIGNED_IN" || evt === "SIGNED_OUT") {
        setTimeout(() => verify(session), 0);
      }
    });
    return () => data.subscription.unsubscribe();
  }, [verify]);

  const value = {
    ...state,
    signIn: async (email, password) => {
      setState((s) => ({ ...s, error: "", loading: true }));
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        const banned = error.code === "user_banned" || /banned/i.test(error.message);
        setState({ loading: false, staff: null, error: banned ? "Access Denied: this staff account has been deactivated." : "Invalid email or password." });
      }
    },
    signOut: () => supabase.auth.signOut(),
    markReset: () => setState((s) => ({ ...s, staff: { ...s.staff, must_reset_password: false } })),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const Loading = () => (
  <div className="staff-root flex min-h-screen items-center justify-center" data-testid="staff-loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--s-line)] border-t-[var(--s-accent)]" />
  </div>
);

export const RequireStaff = ({ children }) => {
  const { loading, staff } = useStaff();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!staff) return <Navigate to="/staff/login" replace />;
  if (staff.must_reset_password && loc.pathname !== "/staff/reset-password") {
    return <Navigate to="/staff/reset-password" replace />;
  }
  return children;
};

export const RequireSuper = ({ children }) => {
  const { staff } = useStaff();
  if (staff?.role !== "super_admin") return <Navigate to="/staff/overview" replace />;
  return children;
};
